/**
 * The curated font files (decision 31): every font a theme can name (`FONT_IDS`, part 05) ships in four static
 * faces, one file each in `packages/layout/fonts/`, with its OFL licence beside it (`fonts/SOURCES.md` says where
 * each came from). A face is loaded only when text needs it (lazy loading, decision 31).
 */
import { FONT_IDS } from '../theme/fonts.ts';
import type { BookLanguage } from '../theme/metadata.ts';
import type { FontId } from '../theme/fonts.ts';
import { frozen } from '../theme/record.ts';

/** The four faces of every curated font: emphasis is italic, strong emphasis bold (decision 35). */
export const FONT_STYLES = frozen(['regular', 'italic', 'bold', 'bold-italic'] as const);

/** One of a font's faces. */
export type FontStyle = (typeof FONT_STYLES)[number];

/** A face: a font and one of its styles. */
export interface FaceKey {
  readonly font: FontId;
  readonly style: FontStyle;
}

/** Every curated face, font by font in `FONT_IDS` order, styles in `FONT_STYLES` order. */
export const FACES: readonly FaceKey[] = frozen(FONT_IDS.flatMap((font) => FONT_STYLES.map((style) => ({ font, style }))));

/** The face for text that is italic, bold, both or neither. */
export function faceStyle(italic: boolean, bold: boolean): FontStyle {
  if (bold) return italic ? 'bold-italic' : 'bold';
  return italic ? 'italic' : 'regular';
}

/** A face's file name in `packages/layout/fonts/`, which is also its stable name for caching. */
export function faceFile(face: FaceKey): string {
  return `${face.font}-${face.style}.ttf`;
}

/** A font's licence file in `packages/layout/fonts/`: every curated font is under the SIL Open Font License 1.1. */
export function licenceFile(font: FontId): string {
  return `${font}-OFL.txt`;
}

/**
 * The OpenType language system each book language shapes with, so a font's local forms apply (`locl`: the
 * Dutch `íj`, the Catalan `l·l`, Polish accents). A font without that language system uses its default one.
 */
export const OPENTYPE_LANGUAGES: Readonly<Record<BookLanguage, string>> = frozen({
  'en-US': 'ENG ',
  'en-GB': 'ENG ',
  de: 'DEU ',
  fr: 'FRA ',
  es: 'ESP ',
  it: 'ITA ',
  pt: 'PTG ',
  nl: 'NLD ',
  sv: 'SVE ',
  nb: 'NOR ',
  nn: 'NYN ',
  da: 'DAN ',
  fi: 'FIN ',
  pl: 'PLK ',
  cs: 'CSY ',
  ca: 'CAT ',
});
